import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'

import { ConnectionTypes } from 'config/constants'
import { changeDriveConnectionType, getDriveConnectionFields, getDriveRemotePathField, normalizeDriveForSave, TYPE_SPECIFIC_DRIVE_KEYS } from 'config/driveConnectionFields'
import { formatSharePointSites, sharePointSiteRows, siteNameFromUrl, validateSharePointSiteUrl } from 'config/sharePointSites'
import {
  useConfiguration,
  useCreateConfigurationDrive,
  useDeleteConfigurationDrive,
  useUpdateConfigurationDrive,
} from 'query/useConfigurations'
import {
  Alert,
  Button,
  CheckboxField,
  ConfirmNameModal,
  DriveLogo,
  Field,
  Form,
  FormActions,
  FormCard,
  Icon,
  IconButton,
  Input,
  LoadingIndicator,
  PageContent,
  PageHeader,
  Select,
} from 'ui'

import parseJSONField from '../parseJSONField'

const initialDriveState = {
  mountpoint: 'Z:',
  name: '',
  reconnectAtLogon: true,
  region: '',
  type: '',
}

const EMPTY_SITE_ROW = { name: '', url: '' }

const FIRST_WINDOWS_DRIVE_LETTER = 'D'
const LAST_WINDOWS_DRIVE_LETTER = 'Z'
const DEFAULT_WINDOWS_MOUNTPOINT = 'Z:'
const validWindowsMountpointPattern = new RegExp(`^[${FIRST_WINDOWS_DRIVE_LETTER}-${LAST_WINDOWS_DRIVE_LETTER}]:$`)

const WINDOWS_DRIVE_LETTERS = Array.from(
  { length: LAST_WINDOWS_DRIVE_LETTER.charCodeAt(0) - FIRST_WINDOWS_DRIVE_LETTER.charCodeAt(0) + 1 },
  (_, index) => String.fromCharCode(FIRST_WINDOWS_DRIVE_LETTER.charCodeAt(0) + index),
)

const normalizeWindowsMountpoint = mountpoint => {
  const normalizedMountpoint = (mountpoint || '').toUpperCase()

  return validWindowsMountpointPattern.test(normalizedMountpoint) ? normalizedMountpoint : DEFAULT_WINDOWS_MOUNTPOINT
}

export const DRIVE_SAVE_ERROR_MESSAGE = 'Failed to save drive. Please try again.'

export const validateDrive = drive => {
  const errors = {}
  if (!ConnectionTypes[drive.type]) {
    errors.type = 'Choose a connection type.'
  }

  if (!drive.name?.trim()) {
    errors.name = 'Enter a nickname.'
  }

  for (const field of getDriveConnectionFields(drive.type)) {
    if (field.required && !drive[field.name]?.trim()) {
      errors[field.name] = field.requiredMessage || `Enter a ${field.label.toLowerCase()}.`
    }
  }

  return errors
}

export const DeploymentsEditDrive = ({ isNew: isNewDrive = false }) => {
  const navigate = useNavigate()
  const { driveId, id: deploymentId } = useParams()
  const [drive, setDrive] = useState(initialDriveState)
  // SharePoint sites as { url, name } rows. They become the region key on save.
  const [sharePointSites, setSharePointSites] = useState([])
  // Keys present on the drive as loaded. Those are never dropped on save, even when blank.
  const [loadedDriveKeys, setLoadedDriveKeys] = useState(new Set())
  const [fieldErrors, setFieldErrors] = useState({})
  const [saveError, setSaveError] = useState('')
  const [loadedDriveKey, setLoadedDriveKey] = useState(isNewDrive ? `${deploymentId}:${driveId}` : null)

  const { data: deployment, invalidateQuery, isLoading: isDeploymentLoading } = useConfiguration(deploymentId, {
    enabled: Boolean(deploymentId) && !isNewDrive,
  })

  const handleMutationSuccess = () => {
    invalidateQuery()
    navigate(`/configurations/${deploymentId}`)
  }

  const handleMutationError = error => {
    setSaveError(error?.message || DRIVE_SAVE_ERROR_MESSAGE)
  }

  const createDriveMutation = useCreateConfigurationDrive(deploymentId, {
    onError: handleMutationError,
    onSuccess: handleMutationSuccess,
  })

  const updateDriveMutation = useUpdateConfigurationDrive(deploymentId, driveId, {
    onError: handleMutationError,
    onSuccess: handleMutationSuccess,
  })

  const deleteDriveMutation = useDeleteConfigurationDrive(deploymentId, driveId, {
    onSuccess: handleMutationSuccess,
  })

  const serializedDrives = deployment?.favorites

  useEffect(() => {
    const driveKey = `${deploymentId}:${driveId}`

    if (isNewDrive) {
      setDrive(initialDriveState)
      setSharePointSites([])
      setLoadedDriveKeys(new Set())
      setLoadedDriveKey(driveKey)
      return
    }

    const drives = parseJSONField(serializedDrives, [])
    const selectedDrive = drives.find(entry => String(entry.id) === driveId)

    if (selectedDrive) {
      setDrive({
        ...initialDriveState,
        ...selectedDrive,
        mountpoint: normalizeWindowsMountpoint(selectedDrive.mountpoint),
      })

      setSharePointSites(selectedDrive.type === 'sharepoint' ? sharePointSiteRows(selectedDrive.region) : [])
      setLoadedDriveKeys(new Set(Object.keys(selectedDrive)))
    } else {
      setDrive(initialDriveState)
      setSharePointSites([])
      setLoadedDriveKeys(new Set())
    }

    setLoadedDriveKey(driveKey)
  }, [serializedDrives, deploymentId, driveId, isNewDrive])

  const clearFieldError = field => {
    setFieldErrors(currentErrors => {
      if (!currentErrors[field]) {
        return currentErrors
      }

      const remainingErrors = { ...currentErrors }
      delete remainingErrors[field]

      return remainingErrors
    })
  }

  const handleChange = (event, field, value = null) => {
    clearFieldError(field)
    setDrive(currentDrive => ({
      ...currentDrive,
      [field]: value !== null ? value : event.target.value,
    }))
  }

  // Always show at least one row so the admin sees where a site goes. Blank rows are ignored.
  const siteRows = sharePointSites.length > 0 ? sharePointSites : [EMPTY_SITE_ROW]

  const clearSiteErrors = () => {
    setFieldErrors(currentErrors => Object.fromEntries(Object.entries(currentErrors).filter(([key]) => !key.startsWith('sites.'))))
  }

  const handleChangeSite = (index, field, value) => {
    clearFieldError(`sites.${index}`)
    setSharePointSites(siteRows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)))
  }

  const handleAddSite = () => {
    setSharePointSites([...siteRows, EMPTY_SITE_ROW])
  }

  const handleRemoveSite = index => {
    clearSiteErrors()
    setSharePointSites(siteRows.filter((_, rowIndex) => rowIndex !== index))
  }

  const handleChangeChecked = (event, field) => {
    setDrive(currentDrive => ({
      ...currentDrive,
      [field]: event.target.checked,
    }))
  }

  const handleSaveDrive = () => {
    const connectionType = ConnectionTypes[drive.type]
    // Secrets are never saved to the configuration. Users enter their own on the device.
    const driveWithoutSecrets = { ...drive }
    delete driveWithoutSecrets.password
    const normalizedDrive = normalizeDriveForSave({
      ...driveWithoutSecrets,
      mountpoint: normalizeWindowsMountpoint(drive.mountpoint),
      ...(connectionType?.hideServer && !drive.server && { server: connectionType.server }),
    })

    const isSharePoint = drive.type === 'sharepoint'
    const sites = isSharePoint
      ? siteRows.map(row => ({ name: row.name.trim(), url: row.url.trim() })).filter(row => row.url || row.name)
      : []

    if (isSharePoint) {
      normalizedDrive.region = formatSharePointSites(sites)
    }

    // Never drop a key the drive arrived with. Only a blank key the form itself added is omitted.
    for (const key of TYPE_SPECIFIC_DRIVE_KEYS) {
      if (normalizedDrive[key] === '' && !loadedDriveKeys.has(key)) {
        delete normalizedDrive[key]
      }
    }

    const errors = validateDrive(normalizedDrive)
    if (isSharePoint) {
      siteRows.forEach((row, index) => {
        if (!row.url.trim() && !row.name.trim()) return

        const error = validateSharePointSiteUrl(row.url.trim())
        if (error) errors[`sites.${index}`] = error
      })
    }

    setSaveError('')
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    if (isNewDrive) {
      createDriveMutation.mutate(normalizedDrive)
      return
    }

    updateDriveMutation.mutate(normalizedDrive)
  }

  const handleCancel = () => {
    navigate(`/configurations/${deploymentId}`)
  }

  const isLoading = !isNewDrive && (isDeploymentLoading || loadedDriveKey !== `${deploymentId}:${driveId}`)
  const isSaving = createDriveMutation.isPending || updateDriveMutation.isPending

  const letterNodes = WINDOWS_DRIVE_LETTERS.map(letter => <option key={letter} value={`${letter}:`}>{letter}:</option>)

  let defaultOption = null
  const selectNodes = Object.keys(ConnectionTypes).map(key => {
    const o = {
      key,
      value: key,
      label: (
        <span className="tw:inline-flex tw:items-center tw:gap-2" key={key}>
          <DriveLogo src={ConnectionTypes[key].brandingIcon} />
          <span>{ConnectionTypes[key].name}</span>
        </span>
      ),
    }

    if (key === drive.type) {
      defaultOption = o
    }

    return o
  })

  const connectionFields = getDriveConnectionFields(drive.type)
  const remotePathField = getDriveRemotePathField(drive.type)

  return (
    <>
      <PageHeader
        helpHref="https://docs.expandrive.com/web-management-console#drive-settings"
        helpLabel="Help — Drive Settings"
        subtitle={isNewDrive ? 'Map a remote connection and choose how it appears on team devices.' : 'Update this drive’s connection, mount behavior, and authentication settings.'}
        title={isNewDrive ? 'New Drive' : 'Edit Drive'}
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : <Form className="tw:mx-auto tw:max-w-form" onSubmit={event => { event.preventDefault(); handleSaveDrive() }}>
          {saveError && <Alert color="danger">{saveError}</Alert>}
          <FormCard title="Drive Settings">
            <Field error={fieldErrors.type} label="Connection Type">
              <Select
                name="type"
                onChange={event => {
                  setFieldErrors({})
                  setSharePointSites([])
                  setDrive(currentDrive => changeDriveConnectionType(currentDrive, event.value))
                }}
                options={selectNodes}
                value={defaultOption}
              />
            </Field>
            <Field error={fieldErrors.name} label="Nickname">
              <Input
                name="name"
                onChange={event => handleChange(event, 'name')}
                value={drive.name}
              />
            </Field>
            {/* Connection-type settings, ending with the type's starting-folder field. Field carries
                the vertical rhythm through its bottom margin, which it drops on the last child of its
                container. Rows keep a zero vertical gap so that margin is the only spacing, and an odd
                field count gets an empty cell so the last child is never a lone field with no margin. */}
            <div className="tw:grid tw:grid-cols-2 tw:gap-x-3 tw:form:grid-cols-1">
              {connectionFields.map(({ name, label, helper, required: _required, requiredMessage: _requiredMessage, ...inputProps }) => (
                <Field error={fieldErrors[name]} helper={helper} key={name} label={label}>
                  <Input {...inputProps} name={name} onChange={event => handleChange(event, name)} value={drive[name] || ''} />
                </Field>
              ))}
              {remotePathField && (
                <Field helper={remotePathField.helper} label={remotePathField.label}>
                  <Input name="remotePath" onChange={event => handleChange(event, 'remotePath')} value={drive.remotePath || ''} />
                </Field>
              )}
              {(connectionFields.length + (remotePathField ? 1 : 0)) % 2 === 1 && <div aria-hidden="true" />}
            </div>
            {drive.type === 'sharepoint' && (
              <Field
                helper="Optional. Each site becomes a folder in the drive. Enter the site’s full https:// URL. The display name is the folder name and defaults to the last part of the URL. With no sites, the drive shows the tenant’s root site. Users sign in to SharePoint on their own device."
                label="Sites"
              >
                <>
                  <div className="ds-helper tw:grid tw:grid-cols-[1fr_1fr_2.5rem] tw:gap-3">
                    <span>Site URL</span>
                    <span>Display name (optional)</span>
                    <span />
                  </div>
                  {siteRows.map((row, index) => {
                    const error = fieldErrors[`sites.${index}`]

                    return (
                      <div className="tw:grid tw:grid-cols-[1fr_1fr_2.5rem] tw:items-start tw:gap-3" key={index}>
                        <div className="tw:grid tw:gap-1">
                          <Input
                            aria-invalid={error ? true : undefined}
                            aria-label={`Site URL ${index + 1}`}
                            onChange={event => handleChangeSite(index, 'url', event.target.value)}
                            placeholder="https://contoso.sharepoint.com/sites/Engineering"
                            value={row.url}
                          />
                          {error && <div className="ds-helper tw:text-status-danger-foreground" role="alert">{error}</div>}
                        </div>
                        <Input
                          aria-label={`Display Name ${index + 1}`}
                          onChange={event => handleChangeSite(index, 'name', event.target.value)}
                          placeholder={row.url.trim() ? siteNameFromUrl(row.url.trim()) : 'Engineering'}
                          value={row.name}
                        />
                        <IconButton label={`Remove site ${index + 1}`} onClick={() => handleRemoveSite(index)} title="Remove site" type="button">
                          <Icon as={Trash2} />
                        </IconButton>
                      </div>
                    )
                  })}
                  <div>
                    <Button onClick={handleAddSite} size="sm" type="button" variant="secondary">
                      <Icon as={Plus} size={16} />
                      Add Site
                    </Button>
                  </div>
                </>
              </Field>
            )}
            <div className="tw:grid tw:grid-cols-2 tw:gap-x-3 tw:form:grid-cols-1">
              <Field label="Windows Drive Letter">
                <Input name="mountpoint" onChange={event => handleChange(event, 'mountpoint')} type="select" value={drive.mountpoint}>
                  {letterNodes}
                </Input>
              </Field>
              <div aria-hidden="true" />
            </div>
            <div className="tw:grid tw:gap-1">
              <CheckboxField
                checked={!!drive.reconnectAtLogon}
                helper="Connect this drive when the user signs in."
                label="Connect Automatically"
                onChange={event => handleChangeChecked(event, 'reconnectAtLogon')}
              />
              <CheckboxField
                checked={!!drive.deferAuthorization}
                helper="Wait to authenticate until the drive is first accessed."
                label="Defer Authentication"
                onChange={event => handleChangeChecked(event, 'deferAuthorization')}
              />
              <CheckboxField
                checked={!!drive.attemptSSO}
                helper="Use the user’s existing single sign-on session when available."
                label="Attempt SSO"
                onChange={event => handleChangeChecked(event, 'attemptSSO')}
              />
            </div>
          </FormCard>
          <FormActions className="tw:mt-5">
            {!isNewDrive && (
              <ConfirmNameModal
                actionLabel="Delete"
                actionPendingLabel="Deleting…"
                confirmationAction="delete this drive"
                isPending={deleteDriveMutation.isPending}
                name={drive.name || 'this drive'}
                onConfirm={() => deleteDriveMutation.mutateAsync()}
                title="Delete Drive"
                trigger={<Button className="tw:mr-auto" type="button" variant="danger">Delete Drive</Button>}
              >
                This drive will be removed from the configuration. This action cannot be undone.
              </ConfirmNameModal>
            )}
            <Button onClick={handleCancel} type="button" variant="secondary">Cancel</Button>
            <Button disabled={isSaving} type="submit">{isSaving ? 'Saving…' : 'Save Changes'}</Button>
          </FormActions>
        </Form>}
      </PageContent>
    </>
  )
}
